export function worldToScreen(worldX, worldY, camX, camY, screenW, screenH) {
  return {
    x: worldX - camX + screenW / 2,
    y: worldY - camY + screenH / 2
  };
}

export function screenToWorld(screenX, screenY, camX, camY, screenW, screenH) {
  return {
    x: screenX - screenW / 2 + camX,
    y: screenY - screenH / 2 + camY
  };
}

export function clampCamera(camX, camY, bounds, screenW, screenH) {
  const minX = bounds.minX + screenW / 2;
  const maxX = Math.max(minX, bounds.maxX - screenW / 2);
  const minY = bounds.minY + screenH / 2;
  const maxY = Math.max(minY, bounds.maxY - screenH / 2);

  return {
    x: Math.max(bounds.minX, Math.min(bounds.maxX, camX)),
    y: Math.max(bounds.minY, Math.min(bounds.maxY, camY))
  };
}

export function clampZoom(zoom, minZoom = 0.5, maxZoom = 2.0) {
  return Math.max(minZoom, Math.min(maxZoom, zoom));
}

export const CAMERA_EDGE_PX = 15;
export const CAMERA_PAN_SPEED = 25;

export function createCameraState(initial = {}) {
  return {
    locked: initial.locked !== undefined ? initial.locked : true,
    x: initial.x || 0,
    z: initial.z || 0,
    spaceHeld: initial.spaceHeld || false
  };
}

/** Clamp cameraLock for settings snapshots (SPY-1614). */
export function normalizeCameraLockPreference(raw) {
  if (typeof raw !== 'boolean') return undefined;
  return raw;
}

/** Restore cameraLock from a settings snapshot; omit/invalid → locked (SPY-1714).
 * Boolean false and string 'false' restore unlocked (SPY-1914).
 * Numeric 0 / string '0' restore unlocked; 1 / '1' stay locked (SPY-2014).
 */
export function cameraLockFromSettingsSnapshot(snapshot = {}) {
  const raw = snapshot?.cameraLock;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  const pref = normalizeCameraLockPreference(raw);
  return pref === undefined ? true : pref;
}

export function toggleCameraLock(state, champion = { x: 0, z: 0 }) {
  const locking = !state.locked;
  if (locking) {
    return {
      ...state,
      locked: true,
      x: champion.x,
      z: champion.z
    };
  }
  return {
    ...state,
    locked: false,
    x: champion.x,
    z: champion.z
  };
}

export function getEdgeScrollDirection(mouseX, mouseY, screenW, screenH, edgePx = CAMERA_EDGE_PX) {
  let dx = 0;
  let dz = 0;
  if (mouseX <= edgePx) dx = -1;
  else if (mouseX >= screenW - edgePx) dx = 1;
  if (mouseY <= edgePx) dz = -1;
  else if (mouseY >= screenH - edgePx) dz = 1;
  return { dx, dz };
}

function isFollowing(state) {
  return state.locked || state.spaceHeld;
}

export function updateCameraState(state, input = {}, champion = { x: 0, z: 0 }, dtSeconds = 0) {
  let next = { ...state };

  if (input.spaceHeld !== undefined) {
    next.spaceHeld = !!input.spaceHeld;
  }
  if (input.spaceDown) {
    next.spaceHeld = true;
  }
  if (input.spaceUp) {
    next.spaceHeld = false;
  }

  if (isFollowing(next) || state.spaceHeld) {
    next.x = champion.x;
    next.z = champion.z;
    if (isFollowing(next)) {
      return next;
    }
  }

  const speed = (input.panSpeed !== undefined ? input.panSpeed : CAMERA_PAN_SPEED) * dtSeconds;
  let screenDx = 0;
  let screenDy = 0;
  if (input.arrowLeft) screenDx -= 1;
  if (input.arrowRight) screenDx += 1;
  if (input.arrowUp) screenDy -= 1;
  if (input.arrowDown) screenDy += 1;
  if (input.edgeDx) screenDx += input.edgeDx;
  if (input.edgeDz) screenDy += input.edgeDz;

  const screenLen = Math.hypot(screenDx, screenDy);
  if (screenLen > 0) {
    screenDx /= screenLen;
    screenDy /= screenLen;
  }

  // Isometric basis (camera from +X+Z): screen-right=(+X,-Z), screen-up=(-X,-Z)
  // screenDy: -1 = up, +1 = down (matches edge scroll)
  const dx = (screenDx + screenDy) * speed;
  const dz = (-screenDx + screenDy) * speed;

  next.x += dx;
  next.z += dz;
  return next;
}
